import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { format, isToday } from "date-fns";
import { fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Clock, Minimize2, UserPlus } from "lucide-react";
import { createPortal } from "react-dom";
import { Table, CombinedTable, Reservation, TableAvailability, TableWeekdayAvailability } from "@/types/table";
import { 
  getTableStatus,
  getStatusColor,
  getLinkedTableInfo,
  TableStatus,
  TimeConflictOptions
} from "@/lib/floorPlanUtils";
import { useFloorPlanPositions, TablePosition } from "@/hooks/useFloorPlanPositions";
import { useFloorPlanFixtures } from "@/hooks/useFloorPlanFixtures";
import { useFloorPlanZones, FloorPlanZone } from "@/hooks/useFloorPlanZones";
import { formatDateLocal } from "@/lib/timeFormatUtils";
import { formatCustomerName } from "@/lib/clientUtils";
import { isTableAvailableOnDate } from "@/lib/tableAvailabilityUtils";
import { getBlockedTableIds } from "@/lib/reservationConflictUtils";
import { parseTimeToMinutes } from "@/lib/timeBookingUtils";
import { useReservationSettings } from "@/hooks/useReservationSettings";
import { useFloorPlanViewport } from "@/hooks/useFloorPlanViewport";
import { useEstablishmentSettings } from "@/hooks/useEstablishmentSettings";
import { FloorPlanZoneTabs } from "./FloorPlanZoneTabs";
import { FloorPlanZoomControls } from "./FloorPlanZoomControls";
import { FloorPlanSearch } from "./FloorPlanSearch";
import { CombinedTableOption } from "./CombinedTableSelectionDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LayoutGrid, Move } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDeviceSize } from "@/hooks/useDeviceSize";
import { FloorPlanDragProvider, useFloorPlanDrag } from "@/contexts/FloorPlanDragContext";
import { FixtureType, FloorPlanFixture as FloorPlanFixtureType } from "@/types/fixture";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { FloorPlanReservationPanel } from "./floor-plan/FloorPlanReservationPanel";
import { FloorPlanControlBar } from "./floor-plan/FloorPlanControlBar";
import { FloorPlanLegend } from "./floor-plan/FloorPlanLegend";
import { FloorPlanDialogs } from "./floor-plan/FloorPlanDialogs";
import { FloorPlanCanvas } from "./floor-plan/FloorPlanCanvas";
import { useFloorPlanEditor } from "@/hooks/useFloorPlanEditor";
import { useEstablishment } from "@/contexts/EstablishmentContext";
import { WaitlistBanner } from "./WaitlistBanner";
import { FloorPlanV2 } from "./floor-plan-v2/FloorPlanV2";
import { isTimeWithinSlot, reservationOverlapsSlot, serviceSortMinutes } from "@/lib/serviceOrder";

interface TimeSlot {
  start: string;
  end: string;
}

interface SwapReservationInfo {
  id: string;
  customerName: string;
  numberOfPeople: number;
  tableName: string;
  tableId: string | null;
  combinedTableId: string | null;
}

interface WaitlistReservation {
  id: number;
  supabaseId: string;
  name: string;
  persons: number;
  numberOfChildren: number;
  createdAt: Date;
  phone: string;
  notes?: string;
  tableName?: string;
  source?: string;
  hasStroller?: boolean;
  time?: string;
}

interface FloorPlanViewProps {
  tables: Table[];
  combinedTables: CombinedTable[];
  reservations: Reservation[];
  selectedDate: Date;
  timeSlots: TimeSlot[];
  availabilities?: TableAvailability[];
  weekdayAvailabilities?: TableWeekdayAvailability[];
  onEditReservation?: (reservation: Reservation) => void;
  onPreviewReservation?: (reservation: Reservation) => void;
  onDateChange?: (date: Date) => void;
  onStatusChange?: (reservation: Reservation, status: "arrivé" | "annulé" | "planifié" | "en_attente") => void;
  onTableChange?: (reservationId: string, tableId: string | null, combinedTableId: string | null) => void;
  onLockChange?: (reservationId: string, locked: boolean) => void;
  onSwapTables?: (reservationAId: string, reservationBId: string) => Promise<void> | void;
  establishmentId?: string;
  // Expanded view extras
  waitlistReservations?: WaitlistReservation[];
  onWaitlistAssignTable?: (reservationId: number) => void;
  onWaitlistCancel?: (reservationId: number) => void;
  onWaitlistEdit?: (reservationId: number) => void;
  onWaitlistMarkArrived?: (reservationId: number) => void;
  onOpenAvailability?: () => void;
  onOpenWalkIn?: () => void;
  onCreateAtTable?: (tableId: string, time: string, date: Date) => void;
  reservationSettings?: { bookingMode: 'slot' | 'time'; reservationDurationMinutes: number };
}

const DEFAULT_GRID_COLS = 12;
const DEFAULT_GRID_ROWS = 8;
const PANEL_WIDTH = 260;
const LAYOUT_GAP = 16;
const CARD_PADDING = 32;

export const FloorPlanView = (props: FloorPlanViewProps) => {
  return (
    <FloorPlanDragProvider>
      <FloorPlanViewContent {...props} />
    </FloorPlanDragProvider>
  );
};

const FloorPlanViewContent = ({
  tables,
  combinedTables,
  reservations,
  selectedDate,
  timeSlots,
  availabilities = [],
  weekdayAvailabilities = [],
  onEditReservation,
  onPreviewReservation,
  onDateChange,
  onStatusChange,
  onTableChange,
  onLockChange,
  onSwapTables,
  establishmentId,
  waitlistReservations = [],
  onWaitlistAssignTable,
  onWaitlistCancel,
  onWaitlistEdit,
  onWaitlistMarkArrived,
  onOpenAvailability,
  onOpenWalkIn,
  onCreateAtTable,
}: FloorPlanViewProps) => {
  const { dragState, startDrag, endDrag } = useFloorPlanDrag();
  const { isMobile, isTablet, isTabletPortrait, windowWidth } = useDeviceSize();
  const { settings: reservationSettings } = useReservationSettings({ establishmentId });
  const { settings: estSettings, isLoading: estSettingsLoading, error: estSettingsError } = useEstablishmentSettings(establishmentId);

  // Diagnostic V2 — visible dans la console pour confirmer la bascule.
  useEffect(() => {
    if (!establishmentId) return;
    console.info('[FloorPlanView] V2 gating', {
      establishmentId,
      establishmentName: estSettings?.name,
      floorPlanV2Enabled: estSettings?.floorPlanV2Enabled,
      estSettingsLoading,
      estSettingsError: estSettingsError ? String(estSettingsError) : null,
    });
  }, [establishmentId, estSettings?.name, estSettings?.floorPlanV2Enabled, estSettingsLoading, estSettingsError]);
  
  const gridCols = DEFAULT_GRID_COLS;
  const gridRows = DEFAULT_GRID_ROWS;
  
  // === Zone state ===
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const previousZoneIdRef = useRef<string | null>(null);
  const [hasInitializedDefaultZone, setHasInitializedDefaultZone] = useState(false);
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<FloorPlanZone | null>(null);
  const [showLayoutEditor, setShowLayoutEditor] = useState(false);

  // === Edit mode state ===
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [localPositions, setLocalPositions] = useState<TablePosition[]>([]);
  const [freeMovementMode, setFreeMovementMode] = useState(false);
  const [selectedFixtureId, setSelectedFixtureId] = useState<string | null>(null);
  const [showAddFixtureDialog, setShowAddFixtureDialog] = useState(false);
  const [showExitConfirmDialog, setShowExitConfirmDialog] = useState(false);
  const [showResetConfirmDialog, setShowResetConfirmDialog] = useState(false);
  const [showFloorPlanRequest, setShowFloorPlanRequest] = useState(false);

  // === Time slot state ===
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [highlightedTableId, setHighlightedTableId] = useState<string | null>(null);

  // === Drag/Drop dialog state ===
  const [showSwapDialog, setShowSwapDialog] = useState(false);
  const [swapDraggedReservation, setSwapDraggedReservation] = useState<SwapReservationInfo | null>(null);
  const [swapTargetReservation, setSwapTargetReservation] = useState<SwapReservationInfo | null>(null);
  const [showCombinedSelectionDialog, setShowCombinedSelectionDialog] = useState(false);
  const [combinedSelectionOptions, setCombinedSelectionOptions] = useState<CombinedTableOption[]>([]);
  const [pendingCombinedDragInfo, setPendingCombinedDragInfo] = useState<{
    draggedReservation: Reservation;
    targetTableId: string;
    targetTableName: string;
  } | null>(null);
  const [showCapacityWarningDialog, setShowCapacityWarningDialog] = useState(false);
  const [capacityWarningInfo, setCapacityWarningInfo] = useState<{
    reservationId: string;
    tableName: string;
    tableCapacity: number;
    numberOfPeople: number;
    tableId: string | null;
    combinedTableId: string | null;
  } | null>(null);

  // === Data hooks ===
  const { 
    positions, 
    loading: positionsLoading, 
    saving: positionsSaving,
    savePositions, 
    resetToDefault,
  } = useFloorPlanPositions(tables, combinedTables, { establishmentId, gridCols, gridRows });

  const {
    fixtures,
    loading: fixturesLoading,
    saving: fixturesSaving,
    createFixture,
    updateFixtureLocal,
    saveFixtures,
    deleteFixture,
  } = useFloorPlanFixtures({ establishmentId });

  const {
    zones,
    defaultZone,
    createZone,
    updateZone,
    deleteZone,
    reorderZones,
    setDefaultZone,
    updateZoneLayout,
    isCreating: isCreatingZone,
    isUpdating: isUpdatingZone,
  } = useFloorPlanZones({ establishmentId });

  // === Computed values ===
  const baseCellSize = useMemo(() => {
    if (isMobile) return 40;
    const availableWidth = windowWidth - PANEL_WIDTH - LAYOUT_GAP - CARD_PADDING - 32;
    const maxCellSize = Math.floor(availableWidth / gridCols);
    if (isTabletPortrait) return Math.min(Math.max(maxCellSize, 45), 50);
    if (isTablet) return Math.min(Math.max(maxCellSize, 50), 55);
    return Math.min(maxCellSize, 60);
  }, [isMobile, isTablet, isTabletPortrait, windowWidth, gridCols]);

  const baseEffectiveGrid = useMemo(() => {
    let maxX = gridCols;
    let maxY = gridRows;
    localPositions.forEach(pos => {
      maxX = Math.max(maxX, pos.x + pos.width + 2);
      maxY = Math.max(maxY, pos.y + pos.height + 2);
    });
    return { cols: maxX, rows: maxY };
  }, [localPositions, gridCols, gridRows]);

  // Zone layout offsets for "All" view
  // Layout des zones figé : ne dépend QUE de la config des zones (pas des tables).
  // Déplacer une table dans la zone A ne déplace plus jamais la zone B.
  // Chaque zone a une taille fixe basée sur son grid_cols/grid_rows configuré.
  const zoneLayoutInfo = useMemo(() => {
    if (zones.length === 0) return { offsets: new Map<string, { x: number; y: number }>(), zoneBounds: new Map<string, { x: number; y: number; width: number; height: number }>(), totalWidth: gridCols, totalHeight: gridRows };

    const zoneSizes = new Map<string, { width: number; height: number }>();
    zones.forEach(zone => {
      // Taille figée depuis la config de la zone (grid_cols/grid_rows), avec un minimum raisonnable
      zoneSizes.set(zone.id, {
        width: Math.max(zone.grid_cols || 12, 6),
        height: Math.max(zone.grid_rows || 8, 4),
      });
    });

    const maxLayoutRow = Math.max(...zones.map(z => z.layout_row || 0));
    const maxLayoutCol = Math.max(...zones.map(z => z.layout_col || 0));
    const colWidths: number[] = Array(maxLayoutCol + 1).fill(0);
    const rowHeights: number[] = Array(maxLayoutRow + 1).fill(0);

    zones.forEach(zone => {
      const size = zoneSizes.get(zone.id)!;
      colWidths[zone.layout_col || 0] = Math.max(colWidths[zone.layout_col || 0], size.width);
      rowHeights[zone.layout_row || 0] = Math.max(rowHeights[zone.layout_row || 0], size.height);
    });

    const colOffsets: number[] = [0];
    const rowOffsets: number[] = [0];
    for (let i = 0; i < colWidths.length; i++) colOffsets.push(colOffsets[i] + colWidths[i] + 2);
    for (let i = 0; i < rowHeights.length; i++) rowOffsets.push(rowOffsets[i] + rowHeights[i] + 2);

    const offsets = new Map<string, { x: number; y: number }>();
    const zoneBounds = new Map<string, { x: number; y: number; width: number; height: number }>();
    zones.forEach(zone => {
      const offset = { x: colOffsets[zone.layout_col || 0], y: rowOffsets[zone.layout_row || 0] };
      offsets.set(zone.id, offset);
      const size = zoneSizes.get(zone.id)!;
      zoneBounds.set(zone.id, { x: offset.x, y: offset.y, width: size.width, height: size.height });
    });

    return { offsets, zoneBounds, totalWidth: colOffsets[colOffsets.length - 1] || gridCols, totalHeight: rowOffsets[rowOffsets.length - 1] || gridRows };
  }, [zones, gridCols, gridRows]);

  const effectiveGrid = useMemo(() => {
    if (selectedZoneId === null && zones.length > 0) {
      return { 
        cols: Math.max(baseEffectiveGrid.cols, zoneLayoutInfo.totalWidth), 
        rows: Math.max(baseEffectiveGrid.rows, zoneLayoutInfo.totalHeight) 
      };
    }
    return baseEffectiveGrid;
  }, [selectedZoneId, zones.length, zoneLayoutInfo, baseEffectiveGrid]);

  // === Viewport hook ===
  const viewport = useFloorPlanViewport({
    establishmentId,
    isEditMode,
    effectiveGrid,
    baseCellSize,
    selectedZoneId,
    savedViewport: estSettings?.floorPlanViewport ?? null,
  });

  // === Anchor viewport (save to DB) ===
  const [isSavingViewport, setIsSavingViewport] = useState(false);
  const handleSaveViewport = useCallback(async () => {
    if (!establishmentId) return;
    setIsSavingViewport(true);
    try {
      const viewportState = viewport.getViewportState();
      const { error } = await supabase
        .from('establishments' as any)
        .update({ floor_plan_viewport: viewportState })
        .eq('id', establishmentId);
      if (error) throw error;
      toast.success('Vue ancrée — cette position sera utilisée sur tous vos appareils');
    } catch (err) {
      console.error('Error saving viewport:', err);
      toast.error('Impossible de sauvegarder la vue');
    } finally {
      setIsSavingViewport(false);
    }
  }, [establishmentId, viewport.getViewportState]);

  // === Sync positions ===
  useEffect(() => {
    setLocalPositions(positions);
    setHasUnsavedChanges(false);
  }, [positions]);

  // === Collision check ===
  const localCheckCollision = useCallback((newPosition: TablePosition, excludeTableId?: string): boolean => {
    if (freeMovementMode) return false;
    return localPositions.some(pos => {
      if (pos.tableId === excludeTableId) return false;
      const noOverlap = 
        newPosition.x >= pos.x + pos.width ||
        newPosition.x + newPosition.width <= pos.x ||
        newPosition.y >= pos.y + pos.height ||
        newPosition.y + newPosition.height <= pos.y;
      return !noOverlap;
    });
  }, [localPositions, freeMovementMode]);

  // Set des table IDs en chevauchement (warning visuel en édition, ne bloque rien)
  const overlappingTableIds = useMemo(() => {
    const ids = new Set<string>();
    for (let i = 0; i < localPositions.length; i++) {
      for (let j = i + 1; j < localPositions.length; j++) {
        const posA = localPositions[i];
        const posB = localPositions[j];
        const overlaps = !(
          posA.x >= posB.x + posB.width || posA.x + posA.width <= posB.x ||
          posA.y >= posB.y + posB.height || posA.y + posA.height <= posB.y
        );
        if (overlaps) {
          ids.add(posA.tableId);
          ids.add(posB.tableId);
        }
      }
    }
    return ids;
  }, [localPositions]);

  const hasOverlappingTables = overlappingTableIds.size > 0;

  const handlePositionChange = useCallback((newPosition: TablePosition) => {
    setLocalPositions(prev => prev.map(p => p.tableId === newPosition.tableId && p.type === newPosition.type ? newPosition : p));
    setHasUnsavedChanges(true);
  }, []);

  // === Floor plan editor (edit mode interactions) ===
  const editor = useFloorPlanEditor({
    positions: localPositions,
    cellSize: viewport.cellSize,
    panOffset: viewport.panOffset,
    setPanOffset: viewport.setPanOffset,
    onPositionChange: handlePositionChange,
    allowOverlap: freeMovementMode,
    checkCollision: localCheckCollision,
  });

  // === Effects ===
  useEffect(() => {
    if (!isEditMode) {
      setSelectedFixtureId(null);
      editor.setSelectedTableId(null);
    }
  }, [isEditMode]);

  // Initialise la zone sélectionnée : localStorage > defaultZone > null
  useEffect(() => {
    if (!hasInitializedDefaultZone && zones.length > 0) {
      const stored = establishmentId ? localStorage.getItem(`floorplan-selected-zone-${establishmentId}`) : null;
      if (stored && zones.some(z => z.id === stored)) {
        setSelectedZoneId(stored);
      } else if (stored === '__all__') {
        setSelectedZoneId(null);
      } else if (defaultZone) {
        setSelectedZoneId(defaultZone.id);
      }
      setHasInitializedDefaultZone(true);
    }
  }, [zones, defaultZone, hasInitializedDefaultZone, establishmentId]);

  // Persiste la sélection de zone par établissement
  useEffect(() => {
    if (!establishmentId || !hasInitializedDefaultZone) return;
    localStorage.setItem(
      `floorplan-selected-zone-${establishmentId}`,
      selectedZoneId === null ? '__all__' : selectedZoneId
    );
  }, [selectedZoneId, establishmentId, hasInitializedDefaultZone]);

  // Note: l'expansion plein écran ne force plus la vue "Toutes les zones".
  // L'utilisateur reste sur la zone qu'il a choisie — c'est lui qui décide.

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Auto-fit on first load
  useEffect(() => {
    viewport.performInitialFit(positionsLoading, localPositions.length);
  }, [positionsLoading, localPositions.length, viewport.performInitialFit]);

  // === Time slot logic ===
  const findCurrentOrNextSlot = useCallback(() => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const nowClock = `${String(Math.floor(currentMinutes / 60)).padStart(2, '0')}:${String(currentMinutes % 60).padStart(2, '0')}`;
    const currentSlot = timeSlots.find(slot => isTimeWithinSlot(nowClock, slot));
    if (currentSlot) return currentSlot;
    return timeSlots.find(slot => currentMinutes < parseTimeToMinutes(slot.start)) || null;
  }, [timeSlots]);

  useEffect(() => {
    if (timeSlots.length > 0 && !selectedTimeSlot) {
      const isToday = formatDateLocal(selectedDate) === formatDateLocal(new Date());
      if (isToday) {
        const slot = findCurrentOrNextSlot();
        setSelectedTimeSlot(slot ? slot.start : timeSlots[timeSlots.length - 1].start);
      } else {
        setSelectedTimeSlot(timeSlots[0].start);
      }
    }
  }, [timeSlots, selectedTimeSlot, selectedDate, findCurrentOrNextSlot]);

  const isCurrentSlotActive = useMemo(() => {
    if (!selectedTimeSlot) return false;
    const isToday = formatDateLocal(selectedDate) === formatDateLocal(new Date());
    if (!isToday) return false;
    const currentSlot = timeSlots.find(ts => ts.start === selectedTimeSlot);
    if (!currentSlot) return false;
    const nowClock = `${String(currentTime.getHours()).padStart(2, '0')}:${String(currentTime.getMinutes()).padStart(2, '0')}`;
    return isTimeWithinSlot(nowClock, currentSlot);
  }, [selectedTimeSlot, selectedDate, timeSlots, currentTime]);

  const handleGoToNow = useCallback(() => {
    const slot = findCurrentOrNextSlot();
    if (slot) setSelectedTimeSlot(slot.start);
  }, [findCurrentOrNextSlot]);

  // === Reservation filtering ===
  const dateReservations = useMemo(() => {
    const dateStr = formatDateLocal(selectedDate);
    return reservations.filter(r => {
      const resDate = r.date instanceof Date ? formatDateLocal(r.date) : r.date;
      return resDate === dateStr;
    });
  }, [reservations, selectedDate]);

  const timeSlotReservations = useMemo(() => {
    if (!selectedTimeSlot) return [];
    const isTimeMode = reservationSettings?.bookingMode === 'time';
    const duration = reservationSettings?.reservationDurationMinutes ?? 120;
    const currentSlot = timeSlots.find(ts => ts.start === selectedTimeSlot);
    
    return dateReservations
      .filter(r => {
        if (r.status === 'annulé') return false;
        if (isTimeMode && currentSlot) {
          return reservationOverlapsSlot(r.startTime.substring(0, 5), duration, currentSlot.start, currentSlot.end);
        }
        return r.startTime.substring(0, 5) === selectedTimeSlot;
      })
      .sort((a, b) => {
        if (isTimeMode) {
          const tc = serviceSortMinutes(a.startTime, timeSlots) - serviceSortMinutes(b.startTime, timeSlots);
          if (tc !== 0) return tc;
        }
        const tA = a.tableId ? tables.find(t => t.id === a.tableId)?.name : a.combinedTableId ? combinedTables.find(ct => ct.id === a.combinedTableId)?.name : '';
        const tB = b.tableId ? tables.find(t => t.id === b.tableId)?.name : b.combinedTableId ? combinedTables.find(ct => ct.id === b.combinedTableId)?.name : '';
        return (tA || '').localeCompare(tB || '');
      });
  }, [dateReservations, selectedTimeSlot, tables, combinedTables, reservationSettings, timeSlots]);

  // === Zone table counts ===
  const tableCountByZone = useMemo(() => {
    const counts: Record<string, number> = { unassigned: 0 };
    zones.forEach(zone => { counts[zone.id] = 0; });
    localPositions.forEach(pos => {
      if (pos.zoneId && counts[pos.zoneId] !== undefined) counts[pos.zoneId]++;
      else counts.unassigned++;
    });
    return counts;
  }, [localPositions, zones]);

  // === Displayed positions & fixtures (with zone offsets) ===
  const displayedPositions = useMemo(() => {
    if (selectedZoneId === null) {
      return localPositions.map(pos => {
        if (!pos.zoneId) return pos;
        const offset = zoneLayoutInfo.offsets.get(pos.zoneId);
        if (!offset) return pos;
        return { ...pos, x: pos.x + offset.x, y: pos.y + offset.y };
      });
    }
    if (selectedZoneId === 'unassigned') return localPositions.filter(p => !p.zoneId);
    return localPositions.filter(p => p.zoneId === selectedZoneId);
  }, [localPositions, selectedZoneId, zoneLayoutInfo]);

  const displayedFixtures = useMemo(() => {
    if (selectedZoneId === null) {
      return fixtures.map(fixture => {
        if (!fixture.zoneId) return fixture;
        const offset = zoneLayoutInfo.offsets.get(fixture.zoneId);
        if (!offset) return fixture;
        return { ...fixture, x: fixture.x + offset.x, y: fixture.y + offset.y };
      });
    }
    if (selectedZoneId === 'unassigned') return fixtures.filter(f => !f.zoneId);
    return fixtures.filter(f => f.zoneId === selectedZoneId);
  }, [fixtures, selectedZoneId, zoneLayoutInfo]);

  // === Drag/Drop logic ===
  const { validDropTargets, swapDropTargets, combinedDropTargetInfo, tablesInCombinedMap } = useMemo(() => {
    if (!dragState.isDragging || !dragState.draggedReservation || !selectedTimeSlot) {
      return { validDropTargets: new Set<string>(), swapDropTargets: new Set<string>(), combinedDropTargetInfo: new Map<string, string>(), tablesInCombinedMap: new Map<string, CombinedTable[]>() };
    }

    const validIds = new Set<string>();
    const swapIds = new Set<string>();
    const combinedInfo = new Map<string, string>();
    const draggedRes = dragState.draggedReservation;
    const isFromCombined = !!draggedRes.combinedTableId;

    const otherReservations = timeSlotReservations.filter(r => r.id !== draggedRes.id);
    const blockedIds = getBlockedTableIds(otherReservations as Reservation[], combinedTables, selectedDate, selectedTimeSlot);

    const tablesCombinedMap = new Map<string, CombinedTable[]>();
    combinedTables.forEach(ct => {
      if (ct.isActive) ct.tableIds.forEach(tid => {
        const existing = tablesCombinedMap.get(tid) || [];
        existing.push(ct);
        tablesCombinedMap.set(tid, existing);
      });
    });

    if (isFromCombined) {
      combinedTables.forEach(ct => {
        if (!ct.isActive || ct.id === draggedRes.combinedTableId) return;
        if (ct.tableIds.some(tid => blockedIds.has(tid))) return;
        const allAvail = ct.tableIds.every(tid => {
          const table = tables.find(t => t.id === tid);
          if (!table || !table.isActive) return false;
          const wa = weekdayAvailabilities.find(w => w.tableId === tid) || null;
          return isTableAvailableOnDate(table, selectedDate, availabilities, wa);
        });
        if (allAvail) {
          ct.tableIds.forEach(tid => { validIds.add(tid); combinedInfo.set(tid, ct.name); });
          validIds.add(ct.id);
        }
      });
      tables.forEach(table => {
        if (!table.isActive || blockedIds.has(table.id) || tablesCombinedMap.has(table.id)) return;
        const wa = weekdayAvailabilities.find(w => w.tableId === table.id) || null;
        if (isTableAvailableOnDate(table, selectedDate, availabilities, wa)) validIds.add(table.id);
      });
    } else {
      tables.forEach(table => {
        if (!table.isActive || table.id === draggedRes.tableId) return;
        const wa = weekdayAvailabilities.find(w => w.tableId === table.id) || null;
        if (!isTableAvailableOnDate(table, selectedDate, availabilities, wa)) return;
        if (blockedIds.has(table.id)) { swapIds.add(table.id); }
        else {
          validIds.add(table.id);
          const combos = tablesCombinedMap.get(table.id);
          if (combos && combos.length > 0) combinedInfo.set(table.id, combos[0].name);
        }
      });
    }

    return { validDropTargets: validIds, swapDropTargets: swapIds, combinedDropTargetInfo: combinedInfo, tablesInCombinedMap: tablesCombinedMap };
  }, [dragState.isDragging, dragState.draggedReservation, selectedTimeSlot, timeSlotReservations, tables, combinedTables, selectedDate, availabilities, weekdayAvailabilities]);

  const findReservationOnTable = useCallback((tableId: string) => 
    timeSlotReservations.find(r => r.tableId === tableId && r.status !== 'annulé'), [timeSlotReservations]);

  const findReservationOnCombinedTable = useCallback((ctId: string) => 
    timeSlotReservations.find(r => r.combinedTableId === ctId && r.status !== 'annulé'), [timeSlotReservations]);

  const getCapacityMatch = useCallback((cap: number, people: number): 'optimal' | 'acceptable' | 'under' => {
    if (cap >= people && cap <= people + 2) return 'optimal';
    if (cap >= people) return 'acceptable';
    return 'under';
  }, []);

  const getTableName = (reservation: Reservation): string => {
    if (reservation.tableId) return tables.find(t => t.id === reservation.tableId)?.name || 'Table inconnue';
    if (reservation.combinedTableId) return combinedTables.find(ct => ct.id === reservation.combinedTableId)?.name || 'Table combinée';
    return 'Non assigné';
  };

  // === Drop handler ===
  const handleDropOnTable = (tableId: string, tableType: 'single' | 'combined') => {
    if (!dragState.draggedReservation || !onTableChange) return;
    const { numberOfPeople, id: reservationId } = dragState.draggedReservation;
    const isFromCombined = !!dragState.draggedReservation.combinedTableId;

    // Swap check
    if (tableType === 'single' && swapDropTargets.has(tableId)) {
      const targetRes = findReservationOnTable(tableId);
      if (targetRes) {
        const targetTable = tables.find(t => t.id === tableId);
        setSwapDraggedReservation({
          id: dragState.draggedReservation.id,
          customerName: formatCustomerName(dragState.draggedReservation.customerFirstName, dragState.draggedReservation.customerLastName),
          numberOfPeople,
          tableName: getTableName(dragState.draggedReservation as Reservation),
          tableId: dragState.draggedReservation.tableId || null,
          combinedTableId: dragState.draggedReservation.combinedTableId || null,
        });
        setSwapTargetReservation({
          id: targetRes.id,
          customerName: formatCustomerName(targetRes.customerFirstName, targetRes.customerLastName),
          numberOfPeople: targetRes.numberOfPeople,
          tableName: targetTable?.name || 'Table inconnue',
          tableId: targetRes.tableId || null,
          combinedTableId: targetRes.combinedTableId || null,
        });
        setShowSwapDialog(true);
        endDrag();
        return;
      }
    }

    // Combined table selection check
    if (tableType === 'single' && !isFromCombined) {
      const tableCombos = tablesInCombinedMap.get(tableId);
      if (tableCombos && tableCombos.length > 0) {
        const targetTable = tables.find(t => t.id === tableId);
        const options = tableCombos.map(ct => {
          const existingRes = findReservationOnCombinedTable(ct.id);
          return { combinedTable: ct, isOccupied: !!existingRes, existingReservation: existingRes, capacityMatch: getCapacityMatch(ct.totalCapacity, numberOfPeople) };
        });
        setPendingCombinedDragInfo({ draggedReservation: dragState.draggedReservation as Reservation, targetTableId: tableId, targetTableName: targetTable?.name || 'Table' });
        setCombinedSelectionOptions(options);
        setShowCombinedSelectionDialog(true);
        endDrag();
        return;
      }
    }

    // Normal drop with capacity check
    if (tableType === 'single') {
      const targetTable = tables.find(t => t.id === tableId);
      if (targetTable && targetTable.capacity < numberOfPeople) {
        setCapacityWarningInfo({ reservationId, tableName: targetTable.name, tableCapacity: targetTable.capacity, numberOfPeople, tableId, combinedTableId: null });
        setShowCapacityWarningDialog(true);
        endDrag();
        return;
      }
      onTableChange(reservationId, tableId, null);
    } else {
      const targetCombined = combinedTables.find(ct => ct.id === tableId);
      if (targetCombined && targetCombined.totalCapacity < numberOfPeople) {
        setCapacityWarningInfo({ reservationId, tableName: targetCombined.name, tableCapacity: targetCombined.totalCapacity, numberOfPeople, tableId: null, combinedTableId: tableId });
        setShowCapacityWarningDialog(true);
        endDrag();
        return;
      }
      onTableChange(reservationId, null, tableId);
    }
    endDrag();
  };

  // === Edit mode handlers ===

  const handleFixturePositionChange = (updatedFixture: FloorPlanFixtureType) => {
    updateFixtureLocal(updatedFixture.id, updatedFixture);
    setHasUnsavedChanges(true);
  };

  const handleAssignToZone = (tableId: string, zoneId: string | null) => {
    setLocalPositions(prev => prev.map(p => p.tableId === tableId ? { ...p, zoneId } : p));
    setHasUnsavedChanges(true);
  };

  const handleAddFixture = async (type: FixtureType, name: string | null, color: string) => {
    if (!establishmentId) return;
    const newFixture = await createFixture({
      establishmentId,
      zoneId: selectedZoneId && selectedZoneId !== 'unassigned' ? selectedZoneId : null,
      name, type,
      x: Math.floor(gridCols / 2) - 1, y: Math.floor(gridRows / 2),
      width: 2, height: 1, color, rotation: 0,
    });
    if (newFixture) setSelectedFixtureId(newFixture.id);
  };

  const handleSave = async () => {
    if (hasOverlappingTables) {
      toast.warning("Des tables se chevauchent ! Vous pouvez sauvegarder quand même, mais cela peut prêter à confusion sur le plan.", { duration: 5000 });
    }
    const posOk = await savePositions(localPositions);
    const fixOk = await saveFixtures();
    if (posOk && fixOk) {
      setHasUnsavedChanges(false);
      setIsEditMode(false);
      setFreeMovementMode(false);
    }
  };

  const handleDiscardChanges = () => {
    setLocalPositions(positions);
    setHasUnsavedChanges(false);
    setIsEditMode(false);
    setFreeMovementMode(false);
    setShowExitConfirmDialog(false);
  };

  const handleExitEditMode = () => {
    if (hasUnsavedChanges) setShowExitConfirmDialog(true);
    else setIsEditMode(false);
  };

  const handleReset = async () => {
    const defaults = await resetToDefault();
    setLocalPositions(defaults);
    setHasUnsavedChanges(true);
    viewport.setZoomLevel(1);
    viewport.setPanOffset({ x: 0, y: 0 });
  };

  // === Dialog handlers ===
  const handleSwapConfirm = () => {
    if (!swapDraggedReservation || !swapTargetReservation || !onTableChange) return;
    onTableChange(swapDraggedReservation.id, swapTargetReservation.tableId, swapTargetReservation.combinedTableId);
    onTableChange(swapTargetReservation.id, swapDraggedReservation.tableId, swapDraggedReservation.combinedTableId);
    toast.success("Tables échangées avec succès");
    setShowSwapDialog(false);
    setSwapDraggedReservation(null);
    setSwapTargetReservation(null);
  };

  const handleCombinedTableSelection = useCallback((combinedTableId: string, swapWithReservationId?: string) => {
    if (!pendingCombinedDragInfo || !onTableChange) return;
    if (swapWithReservationId) {
      const existingRes = timeSlotReservations.find(r => r.id === swapWithReservationId);
      if (existingRes) onTableChange(existingRes.id, pendingCombinedDragInfo.draggedReservation.tableId || null, pendingCombinedDragInfo.draggedReservation.combinedTableId || null);
    }
    onTableChange(pendingCombinedDragInfo.draggedReservation.id, null, combinedTableId);
    toast.success(swapWithReservationId ? "Tables échangées avec succès" : "Table mise à jour");
    setShowCombinedSelectionDialog(false);
    setPendingCombinedDragInfo(null);
    setCombinedSelectionOptions([]);
  }, [pendingCombinedDragInfo, onTableChange, timeSlotReservations]);

  const handleSingleTableFromDialog = useCallback((tableId: string) => {
    if (!pendingCombinedDragInfo || !onTableChange) return;
    const targetTable = tables.find(t => t.id === tableId);
    if (targetTable && targetTable.capacity < pendingCombinedDragInfo.draggedReservation.numberOfPeople) {
      setCapacityWarningInfo({ reservationId: pendingCombinedDragInfo.draggedReservation.id, tableName: targetTable.name, tableCapacity: targetTable.capacity, numberOfPeople: pendingCombinedDragInfo.draggedReservation.numberOfPeople, tableId, combinedTableId: null });
      setShowCapacityWarningDialog(true);
      setShowCombinedSelectionDialog(false);
      setPendingCombinedDragInfo(null);
      setCombinedSelectionOptions([]);
      return;
    }
    onTableChange(pendingCombinedDragInfo.draggedReservation.id, tableId, null);
    toast.success("Table mise à jour");
    setShowCombinedSelectionDialog(false);
    setPendingCombinedDragInfo(null);
    setCombinedSelectionOptions([]);
  }, [pendingCombinedDragInfo, onTableChange, tables]);

  const handleCapacityWarningConfirm = () => {
    if (!capacityWarningInfo || !onTableChange) return;
    onTableChange(capacityWarningInfo.reservationId, capacityWarningInfo.tableId, capacityWarningInfo.combinedTableId);
    setShowCapacityWarningDialog(false);
    setCapacityWarningInfo(null);
  };

  // === Zone handlers ===
  const handleZoneSave = async (zoneData: Partial<FloorPlanZone>) => {
    try {
      if (editingZone) await updateZone(editingZone.id, zoneData);
      else await createZone(zoneData);
      setZoneDialogOpen(false);
      setEditingZone(null);
    } catch (error) { console.error("Error saving zone:", error); }
  };

  const handleZoneDelete = async (zoneId: string) => {
    try {
      await deleteZone(zoneId);
      if (selectedZoneId === zoneId) setSelectedZoneId(null);
    } catch (error) { console.error("Error deleting zone:", error); }
  };

  const handleDeleteFixture = async (id: string) => {
    const success = await deleteFixture(id);
    if (success && selectedFixtureId === id) setSelectedFixtureId(null);
  };

  // === Hide assisted setup after 30 days ===
  const { currentEstablishment } = useEstablishment();
  const hideAssistedSetup = useMemo(() => {
    if (!currentEstablishment?.createdAt) return false;
    const created = new Date(currentEstablishment.createdAt);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return created < thirtyDaysAgo;
  }, [currentEstablishment?.createdAt]);

  // === Search handlers ===
  const handleSelectTable = useCallback((tableId: string) => {
    setHighlightedTableId(tableId);
    setTimeout(() => setHighlightedTableId(null), 3000);
  }, []);

  const handleCenterOnTable = useCallback((tableId: string) => {
    const position = localPositions.find(p => p.tableId === tableId);
    if (!position) return;
    viewport.handleCenterOnTable(position.x, position.y, position.width, position.height);
  }, [localPositions, viewport.handleCenterOnTable]);

  const handleFloorPlanClick = (e: React.MouseEvent) => {
    if (isEditMode && (e.target as HTMLElement).dataset.floorplanBackground) {
      setSelectedFixtureId(null);
    }
  };

  // === Floor Plan v2 short-circuit (pilot establishments only) ===
  // Tant que les paramètres établissement chargent, on n'affiche PAS V1 par défaut :
  // cela évitait un fallback silencieux en V1 pour les établissements pilotes V2.
  if (establishmentId && estSettingsLoading && !estSettings) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-sm text-muted-foreground">
        Chargement du plan de salle…
      </div>
    );
  }
  if (establishmentId && estSettingsError && !estSettings) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-2 text-sm">
        <div className="text-destructive font-medium">Impossible de charger les paramètres de l'établissement.</div>
        <div className="text-muted-foreground">Rechargez la page. Si le problème persiste, contactez le support.</div>
      </div>
    );
  }
  if (estSettings?.floorPlanV2Enabled && establishmentId) {
    return (
      <div className="flex flex-col h-[calc(100dvh-180px)] min-h-[520px]">
        <FloorPlanV2
          establishmentId={establishmentId}
          tables={tables}
          combinedTables={combinedTables}
          reservations={reservations}
          selectedDate={selectedDate}
          timeSlots={timeSlots}
          onDateChange={onDateChange}
          onCreateAtTable={onCreateAtTable}
          onEditReservation={onEditReservation}
          onPreviewReservation={onPreviewReservation}
          onStatusChange={onStatusChange}
          onTableChange={onTableChange}
          onLockChange={onLockChange}
          onSwapTables={onSwapTables}
          reservationSettings={reservationSettings ? {
            bookingMode: reservationSettings.bookingMode,
            reservationDurationMinutes: reservationSettings.reservationDurationMinutes,
          } : undefined}
        />
      </div>
    );
  }

  // === Render ===
  return (
    <>
      {/* Fullscreen overlay when expanded */}
      {viewport.isExpanded && createPortal(
        <div className="fixed inset-0 z-50 bg-background flex flex-col">
          {/* Top bar with controls */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-background shrink-0">
           {/* Compact info header */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-foreground">
                {format(selectedDate, "EEE d MMM", { locale: fr })}
              </span>
              <div className="w-px h-4 bg-border" />
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => {
                    const idx = timeSlots.findIndex(ts => ts.start === selectedTimeSlot);
                    if (idx > 0) setSelectedTimeSlot(timeSlots[idx - 1].start);
                  }}
                  disabled={timeSlots.findIndex(ts => ts.start === selectedTimeSlot) <= 0}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <div className={cn(
                  "flex items-center gap-1.5 px-2 py-0.5 rounded text-sm",
                  isCurrentSlotActive && "bg-green-100 dark:bg-green-900/30"
                )}>
                  <Clock className={cn(
                    "h-3.5 w-3.5",
                    isCurrentSlotActive ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
                  )} />
                  <span className="font-medium">
                    {selectedTimeSlot && timeSlots.find(ts => ts.start === selectedTimeSlot)
                      ? `${selectedTimeSlot} - ${timeSlots.find(ts => ts.start === selectedTimeSlot)?.end}`
                      : 'Aucun'}
                  </span>
                  {isCurrentSlotActive && (
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">• En cours</span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => {
                    const idx = timeSlots.findIndex(ts => ts.start === selectedTimeSlot);
                    if (idx < timeSlots.length - 1) setSelectedTimeSlot(timeSlots[idx + 1].start);
                  }}
                  disabled={timeSlots.findIndex(ts => ts.start === selectedTimeSlot) >= timeSlots.length - 1}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
              <span className="text-xs text-muted-foreground">{timeSlotReservations.length} résa.</span>
              {onOpenAvailability && (
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onOpenAvailability}>
                  <Clock className="w-3.5 h-3.5 mr-1" />
                  Disponibilités
                </Button>
              )}
              {onOpenWalkIn && (
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onOpenWalkIn}>
                  <UserPlus className="w-3.5 h-3.5 mr-1" />
                  Walk-in
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <FloorPlanZoomControls
                zoomLevel={viewport.zoomLevel}
                onZoomChange={viewport.setZoomLevel}
                onResetView={viewport.handleResetView}
                onAnchorView={handleSaveViewport}
                isAnchoring={isSavingViewport}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={viewport.handleFitToScreen}
                className="gap-2"
              >
                <Minimize2 className="h-4 w-4" />
                Réduire
              </Button>
            </div>
          </div>

          {/* Waitlist banner in expanded view */}
          {waitlistReservations.length > 0 && (
            <div className="px-4 pt-2 shrink-0">
              <WaitlistBanner
                waitlistReservations={waitlistReservations}
                onAssignTable={onWaitlistAssignTable || (() => {})}
                onCancel={onWaitlistCancel || (() => {})}
                onEdit={onWaitlistEdit}
                onMarkArrived={onWaitlistMarkArrived}
              />
            </div>
          )}

          {/* Full-screen content: reservation panel + canvas */}
          <div className="flex-1 min-h-0 flex flex-row">
            {/* Reservation panel — left side */}
            <div className="w-[320px] shrink-0 border-r border-border overflow-hidden">
              <FloorPlanReservationPanel
                fullscreen
                timeSlotReservations={timeSlotReservations}
                selectedTimeSlot={selectedTimeSlot}
                timeSlots={timeSlots}
                tables={tables}
                combinedTables={combinedTables}
                reservations={reservations}
                availabilities={availabilities}
                weekdayAvailabilities={weekdayAvailabilities}
                reservationSettings={reservationSettings ? { bookingMode: reservationSettings.bookingMode, reservationDurationMinutes: reservationSettings.reservationDurationMinutes ?? 120 } : null}
                onEditReservation={onEditReservation}
                onStatusChange={onStatusChange}
                onTableChange={onTableChange}
              />
            </div>
            {/* Canvas — right side */}
            <div className="flex-1 min-h-0">
              <FloorPlanCanvas
                isExpanded={true}
                containerRef={viewport.containerRef}
                cellSize={viewport.cellSize}
                baseCellSize={baseCellSize}
                panOffset={viewport.panOffset}
                isPanning={viewport.isPanning}
                isEditMode={false}
                effectiveGrid={effectiveGrid}
                gridCols={gridCols}
                gridRows={gridRows}
                onMouseDown={viewport.handleMouseDown}
                onMouseMove={viewport.handleMouseMove}
                onMouseUp={viewport.handleMouseUp}
                onWheel={viewport.handleWheel}
                onTouchStart={viewport.handleTouchStart}
                onTouchMove={viewport.handleTouchMove}
                onTouchEnd={viewport.handleTouchEnd}
                isTablet={isTablet || isTabletPortrait}
                displayedPositions={displayedPositions}
                displayedFixtures={displayedFixtures}
                tables={tables}
                combinedTables={combinedTables}
                dateReservations={dateReservations}
                selectedTimeSlot={selectedTimeSlot}
                timeSlots={timeSlots}
                currentTime={currentTime}
                reservationSettings={reservationSettings ? { bookingMode: reservationSettings.bookingMode, reservationDurationMinutes: reservationSettings.reservationDurationMinutes ?? 122 } : null}
                isDragging={dragState.isDragging}
                validDropTargets={validDropTargets}
                swapDropTargets={swapDropTargets}
                combinedDropTargetInfo={combinedDropTargetInfo}
                highlightedTableId={highlightedTableId}
                freeMovementMode={freeMovementMode}
                localCheckCollision={localCheckCollision}
                zones={zones}
                selectedFixtureId={null}
                onFixtureSelect={() => {}}
                onFixturePositionChange={() => {}}
                onFixtureDelete={() => {}}
                onPositionChange={handlePositionChange}
                onEditReservation={onEditReservation}
                onStatusChange={onStatusChange}
                onDropOnTable={handleDropOnTable}
                onAssignToZone={handleAssignToZone}
                onFloorPlanClick={() => {}}
                selectedZoneId={selectedZoneId}
                zoneBounds={zoneLayoutInfo.zoneBounds}
                overlappingTableIds={overlappingTableIds}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Normal (non-expanded) layout */}
      <div className="space-y-4">
        <FloorPlanControlBar
          selectedDate={selectedDate}
          onDateChange={onDateChange}
          selectedTimeSlot={selectedTimeSlot}
          onTimeSlotChange={setSelectedTimeSlot}
          timeSlots={timeSlots}
          isCurrentSlotActive={isCurrentSlotActive}
          onGoToNow={handleGoToNow}
          timeSlotReservationCount={timeSlotReservations.length}
          isEditMode={isEditMode}
          onToggleEditMode={() => {
            if (isEditMode) {
              handleExitEditMode();
              editor.setSelectedTableId(null);
            } else {
              setIsEditMode(true);
              if (selectedZoneId === null && zones.length > 0) {
                setSelectedZoneId(defaultZone?.id || zones[0].id);
              }
            }
          }}
          freeMovementMode={freeMovementMode}
          onToggleFreeMovement={() => setFreeMovementMode(!freeMovementMode)}
          hasUnsavedChanges={hasUnsavedChanges}
          onSave={handleSave}
          isSaving={positionsSaving || fixturesSaving}
          onShowAddFixture={() => setShowAddFixtureDialog(true)}
          onShowReset={() => setShowResetConfirmDialog(true)}
          onShowFloorPlanRequest={() => setShowFloorPlanRequest(true)}
          hideAssistedSetup={hideAssistedSetup}
          onExpand={viewport.handleFitToScreen}
          onAnchorView={handleSaveViewport}
          isAnchoring={isSavingViewport}
        />

        <div className="flex flex-col md:flex-row gap-4">
          <Card className="p-4 overflow-hidden flex-shrink min-w-0 flex-1">
            {/* Zone tabs */}
            {(zones.length > 0 || isEditMode) && (
              <div className="mb-3 flex flex-col gap-2 relative z-50">
                {isEditMode && zones.length >= 2 && (
                  <div className="flex justify-start">
                    <Button variant="outline" size="sm" onClick={() => setShowLayoutEditor(true)} className="gap-2">
                      <LayoutGrid className="h-4 w-4" />
                      <span>Disposition des zones</span>
                    </Button>
                  </div>
                )}
                <div className="flex-1">
                  <FloorPlanZoneTabs
                    zones={zones}
                    selectedZoneId={selectedZoneId}
                    onSelectZone={setSelectedZoneId}
                    onCreateZone={() => { setEditingZone(null); setZoneDialogOpen(true); }}
                    onEditZone={(zone) => { setEditingZone(zone); setZoneDialogOpen(true); }}
                    onDeleteZone={handleZoneDelete}
                    onReorderZones={reorderZones}
                    onSetDefaultZone={setDefaultZone}
                    isEditMode={isEditMode}
                    tableCountByZone={tableCountByZone}
                    onMoveTableToZone={handleAssignToZone}
                    isDraggingReservation={dragState.isDragging}
                  />
                </div>
              </div>
            )}

            {/* Zoom controls - only in edit mode */}
            {isEditMode && (
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Move className="w-4 h-4" />
                  <span>Glisser le fond pour naviguer, molette pour zoomer</span>
                  {tables.length > 0 && (
                    <span className="ml-2 text-xs bg-muted/50 px-2 py-0.5 rounded">
                      Cliquez une table pour la sélectionner, glissez pour déplacer
                    </span>
                  )}
                </div>
                <FloorPlanZoomControls
                  zoomLevel={viewport.zoomLevel}
                  onZoomChange={viewport.setZoomLevel}
                  onResetView={viewport.handleResetView}
                  onAnchorView={handleSaveViewport}
                  isAnchoring={isSavingViewport}
                />
              </div>
            )}

            {/* Canvas */}
            <FloorPlanCanvas
              isExpanded={viewport.isExpanded}
              containerRef={viewport.containerRef}
              cellSize={viewport.cellSize}
              baseCellSize={baseCellSize}
              panOffset={viewport.panOffset}
              isPanning={viewport.isPanning}
              isEditMode={isEditMode}
              effectiveGrid={effectiveGrid}
              gridCols={gridCols}
              gridRows={gridRows}
              onMouseDown={!isEditMode ? viewport.handleMouseDown : undefined}
              onMouseMove={!isEditMode ? viewport.handleMouseMove : undefined}
              onMouseUp={!isEditMode ? viewport.handleMouseUp : undefined}
              onWheel={viewport.handleWheel}
              onTouchStart={viewport.handleTouchStart}
              onTouchMove={viewport.handleTouchMove}
              onTouchEnd={viewport.handleTouchEnd}
              isTablet={isTablet || isTabletPortrait}
              displayedPositions={displayedPositions}
              displayedFixtures={displayedFixtures}
              tables={tables}
              combinedTables={combinedTables}
              dateReservations={dateReservations}
              selectedTimeSlot={selectedTimeSlot}
              timeSlots={timeSlots}
              currentTime={currentTime}
              reservationSettings={reservationSettings ? { bookingMode: reservationSettings.bookingMode, reservationDurationMinutes: reservationSettings.reservationDurationMinutes ?? 122 } : null}
              isDragging={dragState.isDragging}
              validDropTargets={validDropTargets}
              swapDropTargets={swapDropTargets}
              combinedDropTargetInfo={combinedDropTargetInfo}
              highlightedTableId={highlightedTableId}
              freeMovementMode={freeMovementMode}
              localCheckCollision={localCheckCollision}
              zones={zones}
              selectedFixtureId={selectedFixtureId}
              onFixtureSelect={setSelectedFixtureId}
              onFixturePositionChange={handleFixturePositionChange}
              onFixtureDelete={handleDeleteFixture}
              onPositionChange={handlePositionChange}
              onEditReservation={onEditReservation}
              onStatusChange={onStatusChange}
              onDropOnTable={handleDropOnTable}
              onAssignToZone={handleAssignToZone}
              onFloorPlanClick={handleFloorPlanClick}
              editorHandlers={isEditMode ? editor.canvasPointerHandlers : undefined}
              selectedEditTableId={editor.selectedTableId}
              editDragTransform={editor.dragTransform}
              editResizeTransform={editor.resizeTransform}
              onShapeChange={editor.handleShapeChange}
              onRotate={editor.handleRotate}
              selectedZoneId={selectedZoneId}
              zoneBounds={zoneLayoutInfo.zoneBounds}
              overlappingTableIds={overlappingTableIds}
            />
          </Card>

          <FloorPlanReservationPanel
            timeSlotReservations={timeSlotReservations}
            selectedTimeSlot={selectedTimeSlot}
            timeSlots={timeSlots}
            tables={tables}
            combinedTables={combinedTables}
            reservations={reservations}
            availabilities={availabilities}
            weekdayAvailabilities={weekdayAvailabilities}
            reservationSettings={reservationSettings ? { bookingMode: reservationSettings.bookingMode, reservationDurationMinutes: reservationSettings.reservationDurationMinutes ?? 120 } : null}
            onEditReservation={onEditReservation}
            onStatusChange={onStatusChange}
            onTableChange={onTableChange}
          />
        </div>

        <FloorPlanLegend />

        <FloorPlanDialogs
          showAddFixtureDialog={showAddFixtureDialog}
          onShowAddFixtureDialogChange={setShowAddFixtureDialog}
          onAddFixture={handleAddFixture}
          showExitConfirmDialog={showExitConfirmDialog}
          onShowExitConfirmDialogChange={setShowExitConfirmDialog}
          onDiscardChanges={handleDiscardChanges}
          onSaveAndExit={() => { handleSave(); setShowExitConfirmDialog(false); }}
          showResetConfirmDialog={showResetConfirmDialog}
          onShowResetConfirmDialogChange={setShowResetConfirmDialog}
          onResetConfirm={() => { handleReset(); setShowResetConfirmDialog(false); }}
          zoneDialogOpen={zoneDialogOpen}
          onZoneDialogOpenChange={setZoneDialogOpen}
          editingZone={editingZone}
          onZoneSave={handleZoneSave}
          isZoneLoading={isCreatingZone || isUpdatingZone}
          showSwapDialog={showSwapDialog}
          onShowSwapDialogChange={setShowSwapDialog}
          swapDraggedReservation={swapDraggedReservation}
          swapTargetReservation={swapTargetReservation}
          onSwapConfirm={handleSwapConfirm}
          showCombinedSelectionDialog={showCombinedSelectionDialog}
          onShowCombinedSelectionDialogChange={(open) => {
            setShowCombinedSelectionDialog(open);
            if (!open) { setPendingCombinedDragInfo(null); setCombinedSelectionOptions([]); }
          }}
          pendingCombinedDragReservation={pendingCombinedDragInfo?.draggedReservation || null}
          combinedSelectionOptions={combinedSelectionOptions}
          onCombinedTableSelection={handleCombinedTableSelection}
          onSingleTableFromDialog={handleSingleTableFromDialog}
          pendingCombinedTargetTableId={pendingCombinedDragInfo?.targetTableId}
          pendingCombinedTargetTableName={pendingCombinedDragInfo?.targetTableName}
          showCapacityWarningDialog={showCapacityWarningDialog}
          onShowCapacityWarningDialogChange={setShowCapacityWarningDialog}
          capacityWarningInfo={capacityWarningInfo}
          onCapacityWarningConfirm={handleCapacityWarningConfirm}
          onCapacityWarningCancel={() => { setShowCapacityWarningDialog(false); setCapacityWarningInfo(null); }}
          showLayoutEditor={showLayoutEditor}
          onShowLayoutEditorChange={setShowLayoutEditor}
          zones={zones}
          onUpdateZonePosition={updateZoneLayout}
          showFloorPlanRequest={showFloorPlanRequest}
          onShowFloorPlanRequestChange={setShowFloorPlanRequest}
          tables={tables}
        />
      </div>
    </>
  );
};
